<template>
    <div>
        <v-progress-linear v-if="busy" absolute indeterminate></v-progress-linear>

        <card-unit :items="items">
            <template #title>{{ t('user_label_bubble_allowance') }}</template>
        </card-unit>

        <v-card class="app-card mt-4" v-if="data">
            <v-card-title>{{ t('user_label_bubble_grant_credits') }}</v-card-title>
            <v-card-text>
                <p class="text-caption mb-4">{{ t('user_text_bubble_grant_hint', { period: data.period }) }}</p>
                <v-text-field
                    v-model.number="form.credits"
                    type="number"
                    min="1"
                    outlined
                    dense
                    :label="t('user_label_bubble_credits')"
                    :error-messages="form.errors.get('credits')"
                ></v-text-field>
                <v-text-field
                    v-model="form.note"
                    outlined
                    dense
                    :label="t('user_label_bubble_grant_note')"
                    :error-messages="form.errors.get('note')"
                ></v-text-field>
                <v-btn color="primary" class="sm" :disabled="! form.credits || form.busy" @click="submit">
                    {{ t('user_action_bubble_grant') }}
                </v-btn>

                <v-simple-table v-if="data.grants.length > 0" class="mt-6" dense>
                    <thead>
                    <tr>
                        <th>{{ t('user_label_bubble_credits') }}</th>
                        <th>{{ t('user_label_bubble_grant_note') }}</th>
                        <th>{{ t('general_label_created_by') }}</th>
                        <th>{{ t('general_label_created') }}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="grant in data.grants" :key="grant.id">
                        <td>+{{ grant.credits }}</td>
                        <td>{{ grant.note || '-' }}</td>
                        <td>{{ grant.granted_by || '-' }}</td>
                        <td>{{ moment(grant.created_at).format('DD/MM/YYYY HH:mm') }}</td>
                    </tr>
                    </tbody>
                </v-simple-table>
            </v-card-text>
        </v-card>
    </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, reactive, toRefs } from '@vue/composition-api'
import { useI18n } from 'vue-i18n-composable'
import moment from 'moment/moment'
import { useStore } from '@/store'
import { useForm } from '@/core/form'
import CardUnit from '@/components/CardUnit.vue'
import { User } from '@/core/models/user'
import { BubbleAllowance, useUserBubbleAllowanceHttp } from '@/core/api/user-bubble-allowance'

/**
 * SSO-3623: the swimmer's Bubble credits this month — used, the plan they were counted against —
 * and extra credits for the month, which support will be asked for.
 */
export default defineComponent({
    name: 'UserBubbleAllowanceCard',

    components: {
        CardUnit,
    },

    props: {
        user: {
            type: User,
            required: true
        },
    },

    setup(props) {
        const { t } = useI18n()
        const store = useStore()
        const http = useUserBubbleAllowanceHttp(props.user.id)

        const state = reactive({
            busy: true,
            data: <BubbleAllowance | null>null,
        })

        const form = reactive(useForm({ credits: <number | null>null, note: '' }))

        const items = computed(() => {
            if (!state.data) return []

            const a = state.data.allowance

            return [
                { text: t('user_label_bubble_plan'), value: t(`user_text_bubble_plan_${a.plan}`) },
                { text: t('user_label_bubble_credits_used'), value: `${a.credits_used} / ${a.credits_total}` },
                { text: t('user_label_bubble_credits_left'), value: a.credits_left },
                { text: t('user_label_bubble_granted_credits'), value: state.data.granted_credits },
                { text: t('user_label_bubble_questions_asked'), value: a.questions_asked },
                { text: t('user_label_bubble_resets_at'), value: `${moment.parseZone(a.resets_at).format('DD/MM/YYYY HH:mm')} (${state.data.timezone})` },
            ]
        })

        const capture = () => {
            state.busy = true
            http.get()
                .then((data) => { state.data = data })
                .finally(() => { state.busy = false })
        }

        const submit = () => {
            form.usingSubmit(http.grant({ credits: <number>form.credits, note: form.note || undefined }))
                .then((data) => {
                    state.data = data
                    form.credits = null
                    form.note = ''
                    store.dispatch('notify', { color: 'success', message: t('general_notice_data_updated') })
                })
        }

        onMounted(capture)

        return {
            t,
            moment,
            form,
            items,
            submit,
            ...toRefs(state),
        }
    }
})
</script>
